import { Box } from '@chakra-ui/react';
import { Filters } from '@resistapp/client/data-utils/filter-data/filter';
import {
  getMatchingStringsIgnoreCase,
  QueryFilters,
} from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { AllProjectEnvironmentTypesGroup, EnvironmentTypeGroup } from '@resistapp/common/comparable-env-groups';
import { friendlyEnvironmentType } from '@resistapp/common/environment-types';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { Chip, FullProject } from '@resistapp/common/types';
import { sortUniqEnvironmentsAndAddSampleStatus } from '@resistapp/common/utils';
import { InputActionMeta } from 'chakra-react-select';
import { uniq } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FilterBarResistomapSelect as ResistomapSelect } from '../../forms/filter-bar-resistomap-select';
import { resistomapSelectClassNamePrefix } from '../../forms/resistomap-select';

interface Props {
  queryFilters: QueryFilters;
  project: FullProject;
}

export function SampleAndTypeSearch(props: Props) {
  const [inputValue, setInputValue] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectionLabel, setSelectionLabel] = useState('');
  const environments = sortUniqEnvironmentsAndAddSampleStatus(props.project.samplesByUID, props.project.id);
  const noSamplesWithPhrase = 'No samples with phrase';

  // Note hacky extension of Select to a simple search field
  // - the label has to match the input to be shown
  useEffect(() => {
    if (inputValue) {
      const envNames = environments.map(env => env.name);
      const matchingEnvNames = getMatchingStringsIgnoreCase(inputValue, envNames);
      const cnt = matchingEnvNames.length;

      const label =
        cnt > 1
          ? `Select ${cnt} samples with phrase '${inputValue.toLowerCase()}'`
          : cnt === 1
            ? `Select '${matchingEnvNames[0]}'`
            : `${noSamplesWithPhrase} '${inputValue.toLowerCase()}'`;

      setSelectionLabel(label);
    } else {
      setSelectionLabel(menuOpen ? 'Select all (or type to seach)' : 'All samples');
    }
  }, [inputValue, environments, menuOpen]);

  const existingEnvironmentTypes = useMemo(() => uniq(environments.map(e => e.type)), [environments]);

  // Show environment type selector only when there's more than one distinct type.
  const showEnvironmentTypeOptions = existingEnvironmentTypes.length > 1;

  const allOrSelectedOption: Option = {
    label: selectionLabel,
    value: AllProjectEnvironmentTypesGroup.ALL_PROJECT_ENVIRONMENTS,
    kind: 'env',
  };

  // Only include environment types that are actually present in the data
  const environmentTypeOptions: Option[] = showEnvironmentTypeOptions
    ? existingEnvironmentTypes.map(envType => ({
        label: friendlyEnvironmentType(envType),
        value: envType,
        kind: 'env',
      }))
    : [];

  const chipOptions: Option[] = props.project.chips.map((chip: Chip) => ({
    label: `Chip ${chip.name}`,
    value: chip.id,
    kind: 'chip',
  }));

  const separator: Option = { label: '──────────', value: -1, kind: 'separator', isDisabled: true };

  // Separator between the "Select all" option and environment-type options (if shown)
  const envSeparator: Option = { label: '──────────', value: -2, kind: 'separator', isDisabled: true };

  const baseOptions: Option[] = !showEnvironmentTypeOptions
    ? [allOrSelectedOption]
    : selectionLabel.startsWith(noSamplesWithPhrase)
      ? [...environmentTypeOptions, envSeparator, allOrSelectedOption]
      : [allOrSelectedOption, envSeparator, ...environmentTypeOptions];

  const options: Option[] = chipOptions.length > 1 ? [...baseOptions, separator, ...chipOptions] : baseOptions;

  return (
    <Box
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
      className={TestSelectors.TEST_SAMPLE_SEARCH}
    >
      <ResistomapSelect<Option, false>
        options={options}
        size={'md'}
        isClearable={false}
        isDisabled={false}
        value={getSelectedOption(options, props.queryFilters.filters)}
        onChange={option => {
          if (!option) return;
          if (option.kind === 'chip') {
            props.queryFilters.setChipStable(option.value as number);
          } else if (option.value !== AllProjectEnvironmentTypesGroup.ALL_PROJECT_ENVIRONMENTS) {
            props.queryFilters.setEnvironmentTypeGroupStable(option.value as EnvironmentTypeGroup, true);
            props.queryFilters.setChipStable(undefined);
          } else {
            const envNames = environments.map(env => env.name);
            const matchingEnvNames = inputValue ? getMatchingStringsIgnoreCase(inputValue, envNames) : [];
            if (matchingEnvNames.length) {
              props.queryFilters.setMatchingEnvironmentsAcrossTypes(inputValue);
            } else {
              props.queryFilters.setEnvironmentTypeGroupStable(
                AllProjectEnvironmentTypesGroup.ALL_PROJECT_ENVIRONMENTS,
                true,
              );
            }
            props.queryFilters.setChipStable(undefined);
          }
          setInputValue('');
        }}
        classNamePrefix={resistomapSelectClassNamePrefix}
        onInputChange={(newValue: string, actionMeta: InputActionMeta) => {
          if (actionMeta.action === 'input-change') {
            setInputValue(newValue);
          }
        }}
        onMenuOpen={() => {
          setMenuOpen(true);
        }}
        onMenuClose={() => {
          setMenuOpen(false);
        }}
        isOptionDisabled={(option, _selectValue) => option.isDisabled || option.label.startsWith(noSamplesWithPhrase)}
      />
    </Box>
  );
}

type Option = {
  label: string;
  value: number | EnvironmentTypeGroup;
  kind: 'env' | 'chip' | 'separator';
  isDisabled?: boolean;
};

function getSelectedOption(options: Option[], filters: Filters): Option {
  const chipOption = options.find(
    opt => opt.kind === 'chip' && filters.chips.length === 1 && filters.chips[0] === opt.value,
  );
  if (chipOption) return chipOption;
  const envTypeOptions = options.filter(opt => opt.kind === 'env');
  return envTypeOptions.length === 1
    ? options[0]
    : options.find(opt => filters.selectedEnvironmentTypeGroup === opt.value) || options[0];
}
